import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Divider, Drawer, Loader, Panel, Message } from 'rsuite';
import {
  FaWhatsapp,
  FaPlug,
  FaLink,
  FaSyncAlt,
  FaRobot,
  FaBoxOpen,
  FaStore,
  FaHashtag,
  FaRegCopy,
} from 'react-icons/fa';
import { useAuth } from '../../../hooks/auth';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { toastSuccess, toastError } from '../../../toasts';
import { getConpanyService } from '../../../services/company/getCompany.service';
import {
  getConnection,
  getUniserviceConfig,
  saveConnection,
  removeConnection,
} from '../../../services/uniservice/uniservice.service';
import { styles } from './styles';
import EmBreve from '../../../components/EmBreve';
import { temAcessoApiOficial } from '../../../config/apiOficialWhatsapp';

const STEPS = [
  { icon: FaLink, title: 'Conecte o número oficial', text: 'Vinculação direta com a Meta (Embedded Signup), sem QR Code.' },
  { icon: FaRobot, title: 'O robô atende sozinho', text: 'Responde saudação, cardápio e palavras-chave automaticamente.' },
  { icon: FaBoxOpen, title: 'Notifica cada pedido', text: 'Envia status: recebido, em preparo, saiu para entrega e finalizado.' },
];

export default function Conexoes() {
  const { id_empresa } = useAuth();
  const { theme } = useCustomTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connection, setConnection] = useState(null);
  const [company, setCompany] = useState(null);
  const [uniserviceUrl, setUniserviceUrl] = useState('');
  const [iframeOpen, setIframeOpen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  const connected = Boolean(connection?.businessAccountId);
  const apiOficialLiberada = temAcessoApiOficial(id_empresa);

  const load = useCallback(async () => {
    if (!temAcessoApiOficial(id_empresa)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [conn, config, comp] = await Promise.all([
      getConnection(id_empresa),
      getUniserviceConfig(),
      getConpanyService(id_empresa),
    ]);
    setConnection(conn);
    setCompany(comp);
    setUniserviceUrl((conn?.uniserviceUrl || config?.url || '').replace(/\/+$/, ''));
    setLoading(false);
  }, [id_empresa]);

  useEffect(() => {
    load();
  }, [load]);

  const iframeSrc = useMemo(() => {
    if (!uniserviceUrl) return '';
    const loja = company?.NomeFantasia || company?.Nome || `contrato ${id_empresa}`;
    const params = new URLSearchParams({
      companyId: String(id_empresa),
      name: `MenuCheff · ${loja}`,
    });
    if (connection?.businessAccountId) params.set('businessAccountId', connection.businessAccountId);
    return `${uniserviceUrl}/iframe/fb-login?${params.toString()}`;
  }, [uniserviceUrl, id_empresa, connection, company]);

  const persist = useCallback(
    async (payload, successMessage) => {
      setSaving(true);
      try {
        const saved = await saveConnection(id_empresa, { uniserviceUrl, ...payload });
        setConnection(saved);
        if (successMessage) toastSuccess(successMessage);
        return saved;
      } catch {
        toastError('Não foi possível salvar a conexão');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [id_empresa, uniserviceUrl],
  );

  const onMessageRef = useRef();
  onMessageRef.current = (event) => {
    if (!uniserviceUrl || !event.origin || !uniserviceUrl.startsWith(event.origin)) return;
    let data = event.data;
    try {
      data = typeof data === 'string' ? JSON.parse(data) : data;
    } catch {
      return;
    }
    if (data?.type === 'uniservice:fb-login-complete') {
      persist(
        {
          businessAccountId: data.businessAccountId,
          apiKey: data.secret,
          phoneNumber: data.phoneNumber,
          displayName: data.displayName,
          active: true,
        },
        'Conexão vinculada com sucesso',
      );
      setTimeout(() => setIframeOpen(false), 1200);
    } else if (data?.type === 'uniservice:close') {
      setIframeOpen(false);
    }
  };

  useEffect(() => {
    const handler = (event) => onMessageRef.current?.(event);
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const openIframe = () => {
    if (!uniserviceUrl) {
      toastError('UniService não configurado no servidor (UNISERVICE_URL)');
      return;
    }
    setIframeLoading(true);
    setIframeOpen(true);
  };

  const handleUnlink = async () => {
    setSaving(true);
    try {
      await removeConnection(id_empresa);
      setConnection(null);
      toastSuccess('Conexão desvinculada');
    } catch {
      toastError('Não foi possível desvincular');
    } finally {
      setSaving(false);
    }
  };

  const copy = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(String(value));
    toastSuccess(`${label} copiado`);
  };

  if (!apiOficialLiberada) {
    return (
      <div style={styles.page(theme)}>
        <EmBreve
          titulo="Em breve"
          descricao="A conexão com a API Oficial do WhatsApp ainda está em liberação e estará disponível para a sua loja em breve."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.page(theme)}>
        <Loader center size="md" content="Carregando conexão..." />
      </div>
    );
  }

  return (
    <div style={styles.page(theme)}>
      <div style={styles.wrap}>
        {/* Cabeçalho */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title(theme)}>Conexão WhatsApp Oficial</h2>
            <p style={styles.subtitle(theme)}>
              Vincule o seu número oficial (via UniService) para o robô atender pela API oficial da Meta.
              Cada loja tem uma única conexão.
            </p>
          </div>
          <div style={styles.companyChips}>
            <span style={styles.chip(theme)}><FaStore size={12} /> {company?.NomeFantasia || company?.Nome || 'Sua loja'}</span>
            <span style={styles.chip(theme)}><FaHashtag size={12} /> Contrato {company?.NrContrato ?? id_empresa}</span>
          </div>
        </div>

        {/* Card principal */}
        <Panel bordered style={styles.card(theme)} bodyFill>
          <div style={styles.cardHeader}>
            <div style={styles.iconCircle(connected)}>
              <FaWhatsapp size={26} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.cardTitle(theme)}>
                {connected ? connection.displayName || 'WhatsApp conectado' : 'Nenhuma conexão vinculada'}
              </div>
              <div style={styles.cardMeta(theme)}>
                {connected
                  ? connection.phoneNumber || 'Número oficial vinculado'
                  : 'Conecte um número para o robô começar a atender'}
              </div>
            </div>
            <Badge
              content={connected ? (connection.active ? 'Conectado' : 'Pausado') : 'Desconectado'}
              color={connected ? (connection.active ? 'green' : 'orange') : 'red'}
            />
          </div>

          <Divider style={{ margin: 0 }} />

          {connected ? (
            <div style={styles.cardBody}>
              <div style={styles.infoGrid}>
                <Info label="Número" value={connection.phoneNumber || '—'} theme={theme} onCopy={() => copy(connection.phoneNumber, 'Número')} />
                <Info label="Nome de exibição" value={connection.displayName || '—'} theme={theme} />
                <Info label="ID da conta (WABA)" value={connection.businessAccountId} theme={theme} onCopy={() => copy(connection.businessAccountId, 'ID')} />
                <Info label="Atendimento" value={connection.active ? 'Ativo' : 'Pausado'} theme={theme} />
              </div>

              <div style={styles.hintRow(theme)}>
                Atendimento automático, template de pedido e espelho ficam em <b>Templates → Configurações</b>.
              </div>

              <div style={styles.actions}>
                <Button appearance="ghost" onClick={openIframe} disabled={saving} startIcon={<FaSyncAlt />}>Reconfigurar conexão</Button>
                <Button appearance="subtle" color="red" onClick={handleUnlink} disabled={saving}>Desvincular</Button>
              </div>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon(theme)}><FaPlug size={30} /></div>
              <div style={styles.emptyTitle(theme)}>Vincule o WhatsApp oficial da loja</div>
              <p style={styles.emptyText(theme)}>
                É a API oficial da Meta (não é QR Code). A conexão é feita em poucos cliques e libera
                atendimento automático, notificações de pedido e envio de templates.
              </p>
              <Button appearance="primary" size="lg" onClick={openIframe} disabled={saving} startIcon={<FaLink />} style={{ backgroundColor: theme.colors.primary }}>
                Conectar WhatsApp Oficial
              </Button>
            </div>
          )}
        </Panel>

        {/* Como funciona */}
        <div style={styles.sectionLabel(theme)}>Como funciona</div>
        <div style={styles.steps}>
          {STEPS.map((s, i) => (
            <Panel key={i} bordered style={styles.stepCard(theme)} bodyFill>
              <div style={styles.stepIcon(theme)}><s.icon size={18} /></div>
              <div style={styles.stepTitle(theme)}>{s.title}</div>
              <div style={styles.stepText(theme)}>{s.text}</div>
            </Panel>
          ))}
        </div>

        {/* Callout */}
        <Message showIcon type="info" style={{ marginTop: 18 }} header="Sobre a API oficial">
          A API oficial exige um número aprovado pela Meta e vinculado via UniService. As respostas e
          notificações são gerenciadas em <b>Mensagens personalizadas</b>, e os modelos de reengajamento
          (fora da janela de 24h) em <b>Templates</b>.
        </Message>
      </div>

      <Drawer size="sm" placement="right" open={iframeOpen} onClose={() => setIframeOpen(false)}>
        <Drawer.Header>
          <Drawer.Title>Conectar WhatsApp Oficial</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body style={{ padding: 0, position: 'relative' }}>
          {iframeLoading && <Loader center size="md" content="Carregando UniService..." />}
          {iframeSrc && (
            <iframe title="UniService" src={iframeSrc} onLoad={() => setIframeLoading(false)} allow="clipboard-write" style={{ width: '100%', height: '100%', border: 'none' }} />
          )}
        </Drawer.Body>
      </Drawer>
    </div>
  );
}

function Info({ label, value, theme, onCopy }) {
  return (
    <div style={styles.infoItem(theme)}>
      <div style={styles.infoLabel(theme)}>{label}</div>
      <div style={styles.infoValueRow}>
        <span style={styles.infoValue(theme)}>{value}</span>
        {onCopy && value && value !== '—' && (
          <FaRegCopy size={12} style={{ cursor: 'pointer', opacity: 0.5, flexShrink: 0 }} onClick={onCopy} />
        )}
      </div>
    </div>
  );
}
